<template>
  <div class="pricing-calendar-shell">
    <div class="pcs-toolbar">
      <div class="pcs-toolbar-start">
        <slot name="picker"></slot>
      </div>
      <div class="pcs-toolbar-end">
        <div class="btn-group pcs-views" v-if="views.length > 1" role="group" :aria-label="_('View')">
          <button
            v-for="v in views"
            :key="'view-' + v.id"
            type="button"
            :class="'btn ' + (v.id === view ? 'btn-primary' : 'btn-default')"
            :aria-pressed="v.id === view ? 'true' : 'false'"
            v-on:click="$emit('update:view', v.id)"
          >{{ v.label }}</button>
        </div>
      </div>
    </div>
    <slot name="notice"></slot>

    <div v-if="view === 'weekly'" class="pcs-weekly">
      <slot name="weekly"></slot>
    </div>

    <div v-if="view === 'calendar'" class="pcs-calendar">
      <div class="pcs-calendar-head">
        <div class="btn-group pcs-month-nav" role="group">
          <button type="button" class="btn btn-default" :title="_('Previous month')" v-on:click="shiftMonth(-1)"><i class="fa fa-fw fa-chevron-left"></i></button>
          <span class="btn btn-default pcs-month-label" aria-live="polite">
            <span class="pcs-month-label-long">{{ monthLabel }}</span>
            <span class="pcs-month-label-short">{{ shortMonthLabel }}</span>
          </span>
          <button type="button" class="btn btn-default" :title="_('Next month')" v-on:click="shiftMonth(1)"><i class="fa fa-fw fa-chevron-right"></i></button>
        </div>
        <button type="button" class="btn btn-default" v-if="!isCurrentMonth" v-on:click="goToday">{{ _('Today') }}</button>
        <p class="help-block pcs-click-hint">{{ _('Click a day to change it, or drag across several days.') }}</p>
      </div>
      <PricingMonthGrid :month="month" :rangeFrom="rangeFrom" :rangeTo="rangeTo" :cellClass="cellClass" v-on:pick="pickDay" v-on:extend="extendTo">
        <template #default="day">
          <slot name="day" v-bind="day"></slot>
        </template>
      </PricingMonthGrid>
      <slot name="legend"></slot>
    </div>

    <PricingDialog ref="dialog" :title="selectionLabel || _('Apply to the selected dates')" v-on:closed="dialogClosed">
      <div class="row">
        <div class="col-xs-6">
          <div class="form-group">
            <label>{{ _('From') }}</label>
            <DatePicker
              class="restriction-datepicker"
              v-model="rangeFromDate"
              :max-date="rangeToDate"
              :shortLocale="shortLocale"
              :staticInput="true"
              :placeholder="_('Select start date')"
              :field="fieldPrefix + 'from'"
            ></DatePicker>
          </div>
        </div>
        <div class="col-xs-6">
          <div class="form-group">
            <label>{{ _('To') }}</label>
            <DatePicker
              class="restriction-datepicker"
              v-model="rangeToDate"
              :min-date="rangeFromDate"
              :shortLocale="shortLocale"
              :staticInput="true"
              :placeholder="_('Select end date')"
              :field="fieldPrefix + 'to'"
            ></DatePicker>
          </div>
        </div>
      </div>
      <div class="pcs-inline-field" v-if="rangeFrom !== rangeTo">
        <label>{{ _('Weekdays') }}</label>
        <WeekdayToggle :modelValue="rangeDays" v-on:update:modelValue="$emit('update:rangeDays', $event)" />
      </div>
      <slot name="form"></slot>
      <template #actions>
        <slot name="actions"></slot>
      </template>
    </PricingDialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DatePicker from '~/components/DatePicker.vue'
import PricingDialog from '~/components/PricingDialog.vue'
import PricingMonthGrid from '~/components/PricingMonthGrid.vue'
import WeekdayToggle from '~/components/WeekdayToggle.vue'

// Shared layout of the price calendars (CalendarPricingEditor and ActivityRatesEditor): a toolbar
// with the rate plan picker and a Weekly / Calendar view switch, and the month grid with its own
// month navigation. Clicking a day opens the "Apply to the selected dates" form in a dialog, like a
// weekly cell opens its own dialog; dragging over days (or Shift-click) selects several days, and
// the dialog's From / To change the range. The editors keep the range, weekdays and month
// (v-model) and fill the plan picker, day cells, legend, form fields, form buttons (actions) and
// the weekly view through slots. "opening" is emitted just before the dialog opens, "cancel" when a
// press on a day turned into scrolling.
export default {
  components: { DatePicker, PricingDialog, PricingMonthGrid, WeekdayToggle },
  props: {
    view: { type: String, default: 'calendar' },
    views: { type: Array, default: () => [] }, // [{ id: 'weekly' | 'calendar', label }]
    month: { type: Date, required: true },
    rangeFrom: { type: String, default: '' },
    rangeTo: { type: String, default: '' },
    rangeDays: { type: Array, default: () => ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'] },
    cellClass: { type: Function, default: null },
    fieldPrefix: { type: String, default: 'pricingcalendar' }
  },
  emits: ['update:view', 'update:month', 'update:rangeFrom', 'update:rangeTo', 'update:rangeDays', 'opening', 'cancel'],
  data () {
    return {
      anchor: '', // The day a drag started from
      dragging: false,
      dialogOpen: false
    }
  },
  computed: {
    ...mapGetters(['locale']),
    shortLocale () {
      return this.locale ? this.locale.split('_')[0] : 'en'
    },
    hasSelection () {
      return !!(this.rangeFrom && this.rangeTo)
    },
    selectionLabel () {
      if (!this.hasSelection) {
        return ''
      }
      const from = this.fromIso(this.rangeFrom)
      const to = this.fromIso(this.rangeTo)
      const opts = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }
      const fromText = from.toLocaleDateString(this.shortLocale, opts)
      if (this.rangeFrom === this.rangeTo) {
        return fromText
      }
      const n = Math.round((to.getTime() - from.getTime()) / 86400000) + 1
      return fromText + ' – ' + to.toLocaleDateString(this.shortLocale, opts) + ' (' + this._('%s days').replace(/%s/, String(n)) + ')'
    },
    monthLabel () {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return this._(months[this.month.getMonth()]) + ' ' + this.month.getFullYear()
    },
    shortMonthLabel () {
      return this.month.toLocaleDateString(this.shortLocale, { month: 'short', year: '2-digit' })
    },
    isCurrentMonth () {
      const now = new Date()
      return this.month.getFullYear() === now.getFullYear() && this.month.getMonth() === now.getMonth()
    },
    rangeFromDate: {
      get () {
        return this.fromIso(this.rangeFrom)
      },
      set (value) {
        const from = value instanceof Date ? this.toIso(value) : ''
        this.$emit('update:rangeFrom', from)
        if (from && this.rangeTo && from > this.rangeTo) {
          this.$emit('update:rangeTo', from)
        }
      }
    },
    rangeToDate: {
      get () {
        return this.fromIso(this.rangeTo)
      },
      set (value) {
        const to = value instanceof Date ? this.toIso(value) : ''
        this.$emit('update:rangeTo', to)
        if (to && this.rangeFrom && this.rangeFrom > to) {
          this.$emit('update:rangeFrom', to)
        }
      }
    }
  },
  methods: {
    toIso (d) {
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    fromIso (iso) {
      if (!iso) {
        return null
      }
      const parts = String(iso).split('-')
      if (parts.length < 3) {
        return null
      }
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10))
    },
    goToday () {
      const now = new Date()
      this.$emit('update:month', new Date(now.getFullYear(), now.getMonth(), 1))
    },
    shiftMonth (delta) {
      this.$emit('update:month', new Date(this.month.getFullYear(), this.month.getMonth() + delta, 1))
    },
    setRange (a, b) {
      this.$emit('update:rangeFrom', a < b ? a : b)
      this.$emit('update:rangeTo', a < b ? b : a)
    },
    pickDay (date, extend) { // Pressing a day selects it (Shift extends the range); releasing opens the form
      this.anchor = extend && this.rangeFrom ? this.rangeFrom : date
      this.setRange(this.anchor, date)
      this.dragging = true
      window.addEventListener('pointerup', this.endDrag, { once: true })
      window.addEventListener('pointercancel', this.cancelDrag, { once: true })
    },
    extendTo (date) { // Dragging with the mouse over more days
      if (this.dragging) {
        this.setRange(this.anchor, date)
      }
    },
    endDrag () {
      window.removeEventListener('pointercancel', this.cancelDrag)
      if (!this.dragging) {
        return
      }
      this.dragging = false
      this.$nextTick(() => this.openDialog())
    },
    cancelDrag () { // A touch that turned into scrolling
      window.removeEventListener('pointerup', this.endDrag)
      this.dragging = false
      this.clearSelection()
      this.$emit('cancel')
    },
    clearSelection () {
      this.$emit('update:rangeFrom', '')
      this.$emit('update:rangeTo', '')
    },
    /**
     * Open the form for one day. The editors call this for a start time chip of a day, and preselect
     * the start time when "opening" is emitted.
     */
    editDay (date) {
      this.setRange(date, date)
      this.$nextTick(() => this.openDialog())
    },
    openDialog () {
      if (this.hasSelection) {
        this.$emit('opening')
        this.dialogOpen = true
        this.$refs.dialog.open()
      }
    },
    dialogClosed () { // The selection lives while the form is open, like a weekly cell's dialog
      this.dialogOpen = false
      this.clearSelection()
      this.$emit('update:rangeDays', ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'])
    },
    closePanel () { // Also called by the editors after applying the form
      if (this.dialogOpen && this.$refs.dialog) {
        this.$refs.dialog.close()
      }
    }
  },
  beforeUnmount () {
    window.removeEventListener('pointerup', this.endDrag)
    window.removeEventListener('pointercancel', this.cancelDrag)
    this.closePanel()
  }
}
</script>

<style scoped>
.pricing-calendar-shell {
  container-type: inline-size;
  container-name: pricing-calendar
}
.pcs-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start; /* The view switch stays level with the plan picker when the picker has a second line */
  justify-content: space-between;
  gap: 8px 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #f5f5f5;
  border: 1px solid #e3e3e3;
  border-radius: 4px
}
.pcs-toolbar-start,
.pcs-toolbar-end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px
}
.pcs-views,
.pcs-month-nav {
  display: flex;
  flex-wrap: nowrap
}
.pcs-calendar-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;
  margin-bottom: 8px
}
.pcs-calendar-head .pcs-click-hint {
  margin: 0
}
.pcs-month-label,
.pcs-month-label:hover,
.pcs-month-label:active {
  min-width: 140px;
  cursor: default;
  background-color: #fff;
  box-shadow: none
}
.pcs-month-label-short {
  display: none
}
@container pricing-calendar (max-width: 680px) {
  .pcs-month-label,
  .pcs-month-label:hover {
    min-width: 72px
  }
  .pcs-month-label-long {
    display: none
  }
  .pcs-month-label-short {
    display: inline
  }
}
</style>

<style>
/* Price chips and the legend of both price calendars (ActivityRatesEditor, CalendarPricingEditor).
   Not scoped: the chips come from the editors' slots. */
.pricing-calendar-shell .ac-slot {
  display: flex;
  align-items: baseline;
  gap: 0 6px;
  margin-top: 3px;
  padding: 2px 6px;
  border-left: 3px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden
}
.pricing-calendar-shell .ac-time {
  font-weight: 600;
  font-variant-numeric: tabular-nums
}
.pricing-calendar-shell .ac-price {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis
}
.pricing-calendar-shell .ac-clickable {
  cursor: pointer
}
.pricing-calendar-shell .ac-clickable:hover,
.pricing-calendar-shell .ac-clickable:focus-visible {
  outline: 2px solid #337ab7;
  outline-offset: -1px
}
/* Set here (weekly) and set for the date (calendar) stand out */
.pricing-calendar-shell .ac-slot.ac-own,
.pricing-calendar-shell .ac-legend-swatch.ac-own {
  background: #dff0d8;
  border-left-color: #3c763d;
  color: #2b542c
}
.pricing-calendar-shell .ac-slot.ac-override,
.pricing-calendar-shell .ac-legend-swatch.ac-override {
  background: #d9edf7;
  border-left-color: #31708f;
  color: #245269
}
.pricing-calendar-shell .ac-slot.ac-own .ac-price,
.pricing-calendar-shell .ac-slot.ac-override .ac-price {
  font-weight: 600
}
/* Inherited (weekly) and the weekly price on a date (calendar) are quiet */
.pricing-calendar-shell .ac-slot.ac-inherited,
.pricing-calendar-shell .ac-legend-swatch.ac-inherited,
.pricing-calendar-shell .ac-slot.ac-default,
.pricing-calendar-shell .ac-legend-swatch.ac-default {
  background: transparent;
  border-left-color: #d6d6d6;
  color: #888
}
.pricing-calendar-shell .ac-slot.ac-inherited .ac-time,
.pricing-calendar-shell .ac-slot.ac-default .ac-time {
  font-weight: 500;
  color: #666
}
.pricing-calendar-shell .ac-derived .ac-price {
  font-style: italic
}
.pricing-calendar-shell .ac-slot.ac-noprice,
.pricing-calendar-shell .ac-legend-swatch.ac-noprice {
  background: #fcf8e3;
  border-left-color: #c9a85c;
  color: #8a6d3b
}
.pricing-calendar-shell .ac-slot.ac-closed,
.pricing-calendar-shell .ac-legend-swatch.ac-closed {
  background: #f2f2f2;
  border-left-color: #d9534f;
  color: #999
}
.pricing-calendar-shell .ac-slot.ac-inherited-closed {
  background: transparent
}
/* Weekly cells being selected or edited together */
.pricing-calendar-shell .ac-slot.ac-selected {
  box-shadow: 0 0 0 2px #337ab7;
  position: relative;
  z-index: 1
}
.pricing-calendar-shell .ac-person,
.pricing-calendar-shell .ac-all-times {
  margin-right: 2px;
  font-size: 10px;
  opacity: 0.75
}
.pricing-calendar-shell .ac-more {
  margin-left: 1px
}
.pricing-calendar-shell .ac-legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid #e3e3e3;
  border-left-width: 3px;
  border-radius: 2px;
  vertical-align: -2px
}
.pricing-calendar-shell .legend-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #31708f
}
.pricing-calendar-shell .calendar-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 16px;
  margin-top: 8px;
  margin-bottom: 16px
}
/* Tight columns: the currency only in tooltips and the legend */
@container pricing-calendar (min-width: 681px) and (max-width: 960px) {
  .pricing-calendar-shell .ac-cur {
    display: none
  }
  .pricing-calendar-shell .ac-slot {
    padding: 2px 4px;
    gap: 0 3px
  }
}
/* Narrow: chips side by side in the lists of days and weekdays */
@container pricing-calendar (max-width: 680px) {
  .pricing-calendar-shell .pmg-day-content {
    display: flex;
    flex-wrap: wrap;
    gap: 4px
  }
  .pricing-calendar-shell .ac-slot {
    margin-top: 0;
    padding: 3px 8px
  }
}
</style>
